import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select from "react-select";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import { canAddRow, addRowBlockedReason } from "../../util/accounting/formRows";

import toast, { Toaster } from "react-hot-toast";

/** A voucher line is only an entry once it has an account and an amount. */
const VOUCHER_LINE_REQUIRED = [
  { key: "account_id", label: "an account" },
  { key: "amount", label: "an amount", numeric: true },
];

/**
 * A voucher line carries a debit or a credit, never both, so the check looks at
 * whichever side was filled.
 */
const withAmount = (rows) =>
  rows.map((l) => ({ ...l, amount: (parseFloat(l.debit) || 0) || (parseFloat(l.credit) || 0) }));

const VOUCHER_META = {
  PV: {
    label:        "Payment Voucher (PV)",
    description:  "Record a payment made to a supplier, employee, or any payee. Debits an expense/liability account, credits cash or bank.",
    debitHint:    "Expense / Liability account (what you paid for)",
    creditHint:   "Cash or Bank account (where the money came from)",
    contactLabel: "Paid To (Supplier / Payee)",
    contactType:  "supplier",
  },
  RV: {
    label:        "Receipt Voucher (RV)",
    description:  "Record cash or bank received from a customer or other source. Debits cash or bank, credits a revenue or liability account.",
    debitHint:    "Cash or Bank account (where money was received)",
    creditHint:   "Revenue / Liability account (what was received for)",
    contactLabel: "Received From (Customer)",
    contactType:  "customer",
  },
  JV: {
    label:        "Journal Voucher (JV)",
    description:  "General internal adjustments between accounts. No cash movement — used for corrections, accruals, and transfers.",
    debitHint:    "Account to Debit",
    creditHint:   "Account to Credit",
    contactLabel: "Related Contact (optional)",
    contactType:  null,
  },
};

const emptyLine = (type = "debit") => ({ account_id: null, debit: "", credit: "", description: "", project_id: null, type });

export default function NewVoucher() {
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const vType           = searchParams.get("type") || "PV";
  const meta            = VOUCHER_META[vType] || VOUCHER_META.PV;
  const editId          = searchParams.get("edit");
  const isEditMode      = !!editId;

  const [fyList,     setFyList]     = useState([]);
  const [accounts,   setAccounts]   = useState([]);
  const [contacts,   setContacts]   = useState([]);
  const [projects,   setProjects]   = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);

  const [form, setForm] = useState({
    voucher_date: new Date().toISOString().split("T")[0],
    description:  "",
    reference:    "",
    payee_name:   "",
    notes:        "",
    contact_id:   null,   // react-select option
    project_id:   null,   // react-select option
  });

  const [lines, setLines] = useState([emptyLine("debit"), emptyLine("credit")]);
  const [editFYId, setEditFYId] = useState(null);

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    if (!editId) return;
    (async () => {
      setLoadingEdit(true);
      const res = await ajaxAccounting.getVoucher({ id: parseInt(editId) });
      if (res.status === "OK" && res.details) {
        const v = res.details;
        if (v.status !== "draft") {
          toast.error("Only draft vouchers can be edited. Approved vouchers are immutable.");
          navigate(`/accounting/vouchers/${editId}`);
          return;
        }
        setForm({
          voucher_date: v.voucher_date,
          description:  v.description || "",
          reference:    v.reference || "",
          payee_name:   v.payee_name || "",
          notes:        v.notes || "",
          contact_id:   null,
          project_id:   null,
        });
        setLines((v.lines || []).map((l) => ({
          account_id: { value: l.account_id, label: `${l.account_code} — ${l.account_name}` },
          debit: l.debit > 0 ? String(l.debit) : "",
          credit: l.credit > 0 ? String(l.credit) : "",
          description: l.description || "",
          project_id: null,
          type: l.debit > 0 ? "debit" : "credit",
        })));
        setEditFYId(v.financial_year_id);
      } else {
        toast.error(res.message || "Voucher not found.");
        navigate("/accounting/vouchers");
      }
      setLoadingEdit(false);
    })();
  }, [editId]);

  // Once both the fy list and the voucher being edited are loaded, pin the FY selector to the voucher's own year (not editable)
  useEffect(() => {
    if (!editFYId || fyList.length === 0) return;
    const fy = fyList.find((y) => y.id === editFYId);
    setSelectedFY(fy ? { value: fy.id, label: fy.name } : { value: editFYId, label: `FY #${editFYId}` });
  }, [editFYId, fyList]);

  const fetchMeta = async () => {
    const [fyRes, accRes, conRes, projRes] = await Promise.all([
      ajaxAccounting.listFinancialYears({}),
      ajaxAccounting.listAccounts({ postable_only: 1 }),
      ajaxAccounting.listContacts({ type: meta.contactType || undefined }),
      ajaxAccounting.listProjects({ status: "active" }),
    ]);
    if (fyRes.status === "OK") {
      const list = fyRes.details || [];
      setFyList(list);
      const cur = list.find((y) => y.is_current === 1) || list[0];
      if (cur) setSelectedFY({ value: cur.id, label: cur.name });
    }
    if (accRes.status === "OK") setAccounts(accRes.details || []);
    if (conRes.status === "OK") setContacts(conRes.details || []);
    if (projRes.status === "OK") setProjects(projRes.details || []);
  };

  // ── Account option groups by type ─────────────────────────────────────────
  const makeGroupedAccounts = (typeFilter) => {
    const filtered = typeFilter
      ? accounts.filter((a) => typeFilter.includes(a.type))
      : accounts;
    const groups = {};
    filtered.forEach((a) => {
      const g = a.type?.charAt(0).toUpperCase() + a.type?.slice(1) || "Other";
      if (!groups[g]) groups[g] = [];
      groups[g].push({ value: a.id, label: `${a.account_code} — ${a.name}` });
    });
    return Object.entries(groups).map(([label, options]) => ({ label, options }));
  };

  const cashBankAccounts   = makeGroupedAccounts(["asset"]);
  const allAccounts        = makeGroupedAccounts(null);

  const debitOptions  = vType === "PV" ? makeGroupedAccounts(["expense","liability","asset"]) :
                        vType === "RV" ? cashBankAccounts : allAccounts;
  const creditOptions = vType === "PV" ? cashBankAccounts :
                        vType === "RV" ? makeGroupedAccounts(["revenue","liability","equity"]) : allAccounts;

  const contactOptions = contacts.map((c) => ({ value: c.id, label: c.name }));
  const projectOptions = projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }));
  const fyOptions      = fyList.map((y) => ({ value: y.id, label: y.name }));

  // ── Line management ────────────────────────────────────────────────────────
  const setLine = (i, field, val) => {
    const u = [...lines];
    u[i] = { ...u[i], [field]: val };
    // Debit and credit are mutually exclusive per line
    if (field === "debit"  && val) u[i].credit = "";
    if (field === "credit" && val) u[i].debit  = "";
    setLines(u);
  };

  const addDebitLine  = () => setLines([...lines, emptyLine("debit")]);
  const addCreditLine = () => setLines([...lines, emptyLine("credit")]);
  const removeLine = (i) => lines.length > 2 && setLines(lines.filter((_, j) => j !== i));

  const validLines = lines.filter((l) => l.account_id && (parseFloat(l.debit) || parseFloat(l.credit)));
  const totalDebit  = lines.reduce((s, l) => s + (parseFloat(l.debit)  || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced    = Math.abs(totalDebit - totalCredit) < 0.01 && validLines.length >= 1;

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e, andApprove = false) => {
    e.preventDefault();
    if (!selectedFY)            { toast.error("Select a financial year");    return; }
    if (!form.description)      { toast.error("Description is required");    return; }
    if (validLines.length < 1)  { toast.error("Add at least one line item"); return; }
    if (!balanced)              { toast.error(`Debits (${functions.formatCurrency(totalDebit)}) must equal Credits (${functions.formatCurrency(totalCredit)})`); return; }

    setSaving(true);
    const payload = {
      type:              vType,
      voucher_date:      form.voucher_date,
      description:       form.description,
      reference:         form.reference,
      payee_name:        form.contact_id ? form.contact_id.label : form.payee_name,
      notes:             form.notes,
      financial_year_id: selectedFY.value,
      contact_id:        form.contact_id?.value  || null,
      project_id:        form.project_id?.value  || null,
      lines:             validLines.map((l) => ({
        account_id:  l.account_id.value,
        debit:       parseFloat(l.debit)  || 0,
        credit:      parseFloat(l.credit) || 0,
        description: l.description || form.description,
        project_id:  l.project_id?.value || form.project_id?.value || null,
      })),
    };

    const res = await ajaxAccounting.createVoucher(payload);
    if (res.status === "OK") {
      if (andApprove) {
        const appRes = await ajaxAccounting.approveVoucher({ id: res.details?.id });
        if (appRes.status === "OK") toast.success(`${meta.label} created and approved!`);
        else                        toast.success(`${meta.label} created (approve manually).`);
      } else {
        toast.success(`${meta.label} saved as draft!`);
      }
      navigate("/accounting/vouchers");
    } else {
      toast.error(res.message || "Failed to save voucher.");
    }
    setSaving(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!form.description)      { toast.error("Description is required");    return; }
    if (validLines.length < 1)  { toast.error("Add at least one line item"); return; }
    if (!balanced)              { toast.error(`Debits (${functions.formatCurrency(totalDebit)}) must equal Credits (${functions.formatCurrency(totalCredit)})`); return; }

    setSaving(true);
    const payload = {
      id:           parseInt(editId),
      voucher_date: form.voucher_date,
      description:  form.description,
      reference:    form.reference,
      payee_name:   form.contact_id ? form.contact_id.label : form.payee_name,
      notes:        form.notes,
      project_id:   form.project_id?.value || null,
      lines:        validLines.map((l) => ({
        account_id:  l.account_id.value,
        debit:       parseFloat(l.debit)  || 0,
        credit:      parseFloat(l.credit) || 0,
        description: l.description || form.description,
        project_id:  l.project_id?.value || form.project_id?.value || null,
      })),
    };

    const res = await ajaxAccounting.updateVoucher(payload);
    if (res.status === "OK") { toast.success("Voucher updated!"); navigate(`/accounting/vouchers/${editId}`); }
    else toast.error(res.message || "Failed to update voucher.");
    setSaving(false);
  };

  const typeColors = { PV: "#ef4444", RV: "#10b981", JV: "#6366f1" };

  if (loadingEdit) {
    return (
      <AppContainer title="Edit Voucher" previousPageTitle="Vouchers">
        <div className="text-center py-5"><i className="fa-solid fa-spinner fa-spin fa-2x tx-primary"></i></div>
      </AppContainer>
    );
  }

  return (
    <AppContainer
      title={isEditMode ? `Edit ${meta.label}` : meta.label}
      previousPageTitle="Vouchers"
      description={meta.description}
      actions={isEditMode ? [
        { label: saving ? "Updating..." : "Update Voucher", icon: "fa-solid fa-floppy-disk", className: "btn btn-primary", onClick: handleUpdate, disabled: saving },
        { label: "Cancel", icon: "fa-solid fa-xmark", className: "btn btn-light", onClick: () => navigate(`/accounting/vouchers/${editId}`) },
      ] : [
        { label: "Save Draft",       icon: "fa-solid fa-floppy-disk",  className: "btn btn-outline-secondary", onClick: (e) => handleSubmit(e, false), disabled: saving },
        { label: "Save & Approve",   icon: "fa-solid fa-check-double", className: "btn btn-primary",            onClick: (e) => handleSubmit(e, true),  disabled: saving },
        { label: "Cancel",           icon: "fa-solid fa-xmark",        className: "btn btn-light",              onClick: () => navigate("/accounting/vouchers") },
      ]}
    >
      <Toaster />

      {/* Type badge */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="badge px-3 py-2" style={{ background: typeColors[vType], fontSize: "0.85rem" }}>{vType}</span>
        <span className="tx-muted tx-13">{meta.description}</span>
      </div>

      <div className="row">
        {/* ── LEFT: Main form ─────────────────────────────────────────── */}
        <div className="col-lg-8">
          <div className="card custom-card p-4 mb-3">
            <h6 className="fw-semibold mb-3">Voucher Details</h6>
            <div className="row g-3">
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Financial Year <span className="tx-danger">*</span></label>
                <Select options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select year" isSearchable isDisabled={isEditMode} />
                {isEditMode && <div className="tx-11 tx-muted mt-1">Financial year can't be changed when editing.</div>}
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Voucher Date</label>
                <input type="date" className="form-control" value={form.voucher_date}
                  onChange={(e) => setForm({ ...form, voucher_date: e.target.value })} />
              </div>
              <div className="col-12">
                <label className="form-label tx-12 fw-semibold">Description <span className="tx-danger">*</span></label>
                <input className="form-control" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={vType === "PV" ? "e.g. Payment for office supplies" : vType === "RV" ? "e.g. School fees received" : "e.g. Month-end accrual"} />
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Reference / Cheque #</label>
                <input className="form-control" value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="Cheque / EFT / Receipt number" />
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">{meta.contactLabel}</label>
                <Select
                  options={contactOptions}
                  value={form.contact_id}
                  onChange={(v) => setForm({ ...form, contact_id: v, payee_name: v?.label || "" })}
                  placeholder={`Select ${meta.contactType || "contact"}...`}
                  isClearable
                  isSearchable
                />
                {!form.contact_id && (
                  <input className="form-control mt-1" value={form.payee_name}
                    onChange={(e) => setForm({ ...form, payee_name: e.target.value })}
                    placeholder="Or type payee name manually" />
                )}
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Project / Cost Centre</label>
                <Select
                  options={projectOptions}
                  value={form.project_id}
                  onChange={(v) => setForm({ ...form, project_id: v })}
                  placeholder="Link to project (optional)"
                  isClearable
                  isSearchable
                />
              </div>
              <div className="col-12">
                <label className="form-label tx-12 fw-semibold">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
          </div>

          {/* ── Account Lines ──────────────────────────────────────────── */}
          <div className="card custom-card p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-semibold mb-0">Account Lines</h6>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-outline-success" onClick={addDebitLine}
                  disabled={!canAddRow(withAmount(lines), VOUCHER_LINE_REQUIRED)}
                  title={addRowBlockedReason(withAmount(lines), VOUCHER_LINE_REQUIRED) || "Add a debit line"}>
                  <i className="fa-solid fa-plus me-1"></i>Add Dr Line
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={addCreditLine}
                  disabled={!canAddRow(withAmount(lines), VOUCHER_LINE_REQUIRED)}
                  title={addRowBlockedReason(withAmount(lines), VOUCHER_LINE_REQUIRED) || "Add a credit line"}>
                  <i className="fa-solid fa-plus me-1"></i>Add Cr Line
                </button>
              </div>
            </div>

            {/* Column hints */}
            <div className="row mb-2 tx-11 tx-muted px-1">
              <div className="col-4">Account</div>
              <div className="col-2"><span className="tx-success fw-semibold">Debit (Dr)</span> — {meta.debitHint}</div>
              <div className="col-2"><span className="tx-danger fw-semibold">Credit (Cr)</span> — {meta.creditHint}</div>
              <div className="col-2">Project</div>
              <div className="col-1">Description</div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered align-middle" style={{ fontSize: "0.84rem" }}>
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: "50px" }}>Type</th>
                    <th style={{ minWidth: "220px" }}>Account</th>
                    <th style={{ width: "140px" }}>Debit (Dr)</th>
                    <th style={{ width: "140px" }}>Credit (Cr)</th>
                    <th style={{ minWidth: "160px" }}>Project</th>
                    <th>Line Description</th>
                    <th style={{ width: "38px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => {
                    const isDebit = line.type !== "credit";
                    return (
                    <tr key={i}>
                      <td className="text-center align-middle">
                        <span className={`badge ${isDebit ? "bg-success" : "bg-danger"}`}>{isDebit ? "DR" : "CR"}</span>
                      </td>
                      <td>
                        <Select
                          options={isDebit ? debitOptions : creditOptions}
                          value={line.account_id}
                          onChange={(v) => setLine(i, "account_id", v)}
                          placeholder={isDebit ? "Debit account" : "Credit account"}
                          isSearchable
                        />
                      </td>
                      <td>
                        <input type="number" className="form-control form-control-sm" min="0" step="0.01"
                          placeholder="0.00" value={line.debit} disabled={!isDebit}
                          onChange={(e) => setLine(i, "debit", e.target.value)} />
                      </td>
                      <td>
                        <input type="number" className="form-control form-control-sm" min="0" step="0.01"
                          placeholder="0.00" value={line.credit} disabled={isDebit}
                          onChange={(e) => setLine(i, "credit", e.target.value)} />
                      </td>
                      <td>
                        <Select
                          options={projectOptions}
                          value={line.project_id}
                          onChange={(v) => setLine(i, "project_id", v)}
                          placeholder="Project (opt.)"
                          isClearable
                          isSearchable
                        />
                      </td>
                      <td>
                        <input className="form-control form-control-sm" value={line.description}
                          onChange={(e) => setLine(i, "description", e.target.value)}
                          placeholder="Line description" />
                      </td>
                      <td>
                        {lines.length > 2 && (
                          <button type="button" className="btn btn-sm btn-light tx-danger" onClick={() => removeLine(i)}>
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-light fw-semibold">
                  <tr>
                    <td colSpan={2} className="text-end">Totals</td>
                    <td className="tx-success">{functions.formatCurrency(totalDebit)}</td>
                    <td className="tx-danger">{functions.formatCurrency(totalCredit)}</td>
                    <td colSpan={3}>
                      {balanced && validLines.length > 0 && (
                        <span className="badge bg-success"><i className="fa-solid fa-check me-1"></i>Balanced</span>
                      )}
                      {!balanced && validLines.length > 0 && (
                        <span className="badge bg-danger">
                          Difference: {functions.formatCurrency(Math.abs(totalDebit - totalCredit))}
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Summary card ──────────────────────────────────────── */}
        <div className="col-lg-4">
          <div className="card custom-card p-3 mb-3">
            <h6 className="fw-semibold mb-3">Summary</h6>
            <div className="d-flex justify-content-between mb-2 tx-13">
              <span className="tx-muted">Voucher Type</span>
              <span className="badge" style={{ background: typeColors[vType] }}>{meta.label}</span>
            </div>
            {form.contact_id && (
              <div className="d-flex justify-content-between mb-2 tx-13">
                <span className="tx-muted">{meta.contactLabel}</span>
                <span className="fw-semibold">{form.contact_id.label}</span>
              </div>
            )}
            {form.project_id && (
              <div className="d-flex justify-content-between mb-2 tx-13">
                <span className="tx-muted">Project</span>
                <span className="fw-semibold">{form.project_id.label}</span>
              </div>
            )}
            <div className="d-flex justify-content-between mb-2 tx-13">
              <span className="tx-muted">Total Debit</span>
              <span className="fw-bold tx-success">{functions.formatCurrency(totalDebit)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2 tx-13">
              <span className="tx-muted">Total Credit</span>
              <span className="fw-bold tx-danger">{functions.formatCurrency(totalCredit)}</span>
            </div>
            <hr />
            <div className={`alert py-2 tx-12 ${balanced && validLines.length > 0 ? "alert-success" : "alert-warning"}`}>
              {balanced && validLines.length > 0
                ? "✓ Voucher is balanced and ready to post."
                : "⚠ Debits must equal credits before saving."}
            </div>
          </div>

          {/* Account type guide */}
          <div className="card custom-card p-3">
            <h6 className="fw-semibold mb-2 tx-13">Account Guidance</h6>
            <div className="tx-12 tx-muted">
              {vType === "PV" && (
                <>
                  <p className="mb-1"><strong className="tx-success">Debit:</strong> Expense, Asset, or Liability account</p>
                  <p className="mb-0"><strong className="tx-danger">Credit:</strong> Cash in Hand or Bank account</p>
                </>
              )}
              {vType === "RV" && (
                <>
                  <p className="mb-1"><strong className="tx-success">Debit:</strong> Cash in Hand or Bank account</p>
                  <p className="mb-0"><strong className="tx-danger">Credit:</strong> Revenue, Liability, or Equity account</p>
                </>
              )}
              {vType === "JV" && (
                <p className="mb-0">Both sides can use any postable account. Typically used for corrections, accruals, and provisions.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppContainer>
  );
}
